import React, { useEffect, useState } from 'react'
import { Collapse, IconButton, Tab, Tabs } from '@paintscout/ui'
import classNames from 'classnames'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import JobTabContent from './JobTabContent'
import JobActionRow from './JobActionRow'
import WorkOrderContent from './WorkOrderContent'
import { formatEventDateRange } from './dateUtils'
import { useCrmDialogs } from '@paintscout/ui-business'
import type { Task } from '@paintscout/api'
import type { PainterScheduleEvent, TabValue } from './types'
import type { usePainterAdditionalWorkStyles } from './usePainterAdditionalWorkStyles'
import type { usePainterJobCardStyles } from './usePainterJobCardStyles'
import type { usePainterWorkOrderStyles } from './usePainterWorkOrderStyles'

export interface JobCardProps {
  event: PainterScheduleEvent
  activeTab: TabValue
  isExpanded: boolean
  showContactInfo: boolean
  showHours: boolean
  showPrices: boolean
  showPayments: boolean
  showTasksNotesFiles: boolean
  canAddAdditionalWork: boolean
  crewNoteExpanded: boolean
  productsExpanded: boolean
  onToggleExpanded: () => void
  onSetTab: (tab: TabValue) => void
  onToggleCrewNote: () => void
  onToggleProducts: () => void
  callbacks: any
  classes: ReturnType<typeof usePainterJobCardStyles>
  workClasses: ReturnType<typeof usePainterWorkOrderStyles>
  additionalWorkClasses: ReturnType<typeof usePainterAdditionalWorkStyles>
}

export default function JobCard(props: JobCardProps) {
  const {
    event,
    activeTab,
    isExpanded,
    classes,
    workClasses,
    callbacks,
    onToggleExpanded,
    onSetTab,
    showContactInfo,
    showTasksNotesFiles,
    canAddAdditionalWork,
    showPrices,
    showPayments
  } = props
  const { setCrmContext } = useCrmDialogs()
  const [pendingNewTask, setPendingNewTask] = useState<Task | null>(null)

  useEffect(() => {
    setCrmContext({ deal: { id: event.dealId, name: event.dealName, hide: true }, user: { hide: true } })
  }, [event.dealId])

  const approved = event.additionalWork?.filter((item) => item.status === 'approved') ?? []
  const visibleAdditionalWork = canAddAdditionalWork ? event.additionalWork ?? [] : approved
  const pricedAdditionalWork = showPrices
    ? visibleAdditionalWork
    : visibleAdditionalWork.map((item) => ({ ...item, price: undefined }))
  const invoiceTotals = (event.sourceQuote as any)?.totals
  const effectiveShowPayments =
    showPayments && !!event.sourceQuote && (invoiceTotals?.balanceDue == null || invoiceTotals.balanceDue > 0)
  const tabs = [
    { value: 'workOrder' as TabValue, label: 'Work Order' },
    ...(effectiveShowPayments ? [{ value: 'payments' as TabValue, label: 'Payments' }] : []),
    ...(showTasksNotesFiles
      ? [
          { value: 'tasks' as TabValue, label: 'Tasks' },
          { value: 'notes' as TabValue, label: 'Notes' },
          { value: 'files' as TabValue, label: 'Files' }
        ]
      : [])
  ]
  const openTab = (tab: TabValue) => {
    if (!isExpanded) onToggleExpanded()
    onSetTab(tab)
  }
  return (
    <div className={classes.jobCardWrapper}>
      <div className={classNames(classes.jobCardTop, isExpanded && classes.jobCardTopExpanded)}>
        <div className={classes.jobCardHeader} onClick={onToggleExpanded}>
          <span className={classes.jobTitle}>{event.dealName}</span>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              className={classes.jobExpandBtn}
              size="small"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'collapse job details' : 'expand job details'}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </div>
        </div>
        <div className={classes.jobDate}>{formatEventDateRange(event.startDate, event.endDate)}</div>
        <JobActionRow
          event={event}
          showContactInfo={showContactInfo}
          showTasksNotesFiles={showTasksNotesFiles}
          showPayments={showPayments}
          paymentsDisabled={!effectiveShowPayments}
          canAddAdditionalWork={canAddAdditionalWork}
          onAddAdditionalWork={() => callbacks.onAddAdditionalWork?.(event.id)}
          onAddPayment={() => {
            callbacks.onAddPayment?.(event.id)
            openTab('payments')
          }}
          onAddTask={() =>
            callbacks.onAddTask?.(event.id, (task: Task) => {
              setPendingNewTask(task)
              openTab('tasks')
            })
          }
          onAddNote={() => callbacks.onAddNote?.(event.id, () => openTab('notes'))}
          onAddFile={() => callbacks.onAddFile?.(event.id, () => openTab('files'))}
          onDirections={callbacks.onDirections}
          onCall={callbacks.onCall}
          onMessage={callbacks.onMessage}
          classes={classes}
        />
      </div>
      <Collapse show={isExpanded}>
        <div className={classes.jobCardBottom}>
          <Tabs className={classes.jobTabs} value={activeTab} onChange={(_, value: TabValue) => onSetTab(value)}>
            {tabs.map((tab) => (
              <Tab key={tab.value} className={classes.jobTab} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
          {activeTab === 'workOrder' && (
            <WorkOrderContent
              {...props}
              visibleAdditionalWork={pricedAdditionalWork}
              onAddAdditionalWork={() => callbacks.onAddAdditionalWork?.(event.id)}
            />
          )}
          {activeTab !== 'workOrder' && (
            <JobTabContent
              event={event}
              activeTab={activeTab}
              classes={workClasses}
              onAddPayment={activeTab === 'payments' ? () => callbacks.onAddPayment?.(event.id) : undefined}
              pendingNewTask={pendingNewTask}
              onPendingNewTaskConsumed={() => setPendingNewTask(null)}
            />
          )}
        </div>
      </Collapse>
    </div>
  )
}
